import React, { useRef, useState } from 'react'
import { Box, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { useNavigate } from 'react-router-dom'
import { Button, GradientText } from '@/design-system'
import { APP_NAME } from '@/utils/constants'
import { LogoMark } from '../../components/EgowallLogo'
import {
  RoomArtwork,
  RoomFocus,
  RoomWalkthrough3D,
  RoomWalkthroughHandle,
} from '../../components/RoomWalkthrough3D'

const ARTWORKS: RoomArtwork[] = [
  { img: 'a02-beach', ratio: 3 / 4, w: 1.7, side: -1, z: -6, name: 'Golden hour, Amalfi', loves: 11, cms: 2 },
  {
    img: 'a05-birthday',
    ratio: 4 / 3,
    w: 2.4,
    side: 1,
    z: -3,
    name: 'Candles out',
    loves: 24,
    cms: 5,
    voice: true,
  },
  { img: 'a06-baby', ratio: 3 / 4, w: 1.6, side: -1, z: 0, name: 'Tiny hands', loves: 18, cms: 3 },
  { img: 'a07-city', ratio: 3 / 4, w: 1.7, side: 1, z: 3, name: 'Rain walk', loves: 9, cms: 1 },
  {
    img: 'a03-mountains',
    ratio: 4 / 3,
    w: 2.5,
    side: -1,
    z: 6,
    name: 'The Dolomites',
    loves: 14,
    cms: 2,
  },
  {
    img: 'a01-wedding',
    ratio: 4 / 3,
    w: 3.4,
    side: 0,
    z: 17.6,
    name: 'The first dance',
    loves: 128,
    cms: 31,
    pin: true,
    hearts: true,
  },
]

const WIZARD_STEPS = [
  { n: '01', label: 'Scene', sub: '14 ROOMS · 56 LAYOUTS', swatch: 'scene' as const },
  { n: '02', label: 'Frames', sub: '9 PROFILES + NONE', swatch: 'frame' as const },
  { n: '03', label: 'Wall color', sub: '236 TONES', swatch: 'color' as const },
  { n: '04', label: 'Materials', sub: '73 FINISHES', swatch: 'material' as const },
  { n: '05', label: 'Prints', sub: 'YOUR PHOTOS, PLACED', swatch: 'prints' as const },
  { n: '06', label: 'Matting', sub: '4 WIDTHS', swatch: 'matting' as const },
]

const VALUES = [
  {
    title: '⚿ Same key, deeper visit',
    body: 'Guests who scanned the QR at the event walk straight into the gallery — no new link, no new permission.',
  },
  {
    title: '💬 Comments come along',
    body: 'Pinned notes and voice notes appear beside the prints they belong to, floating in the room.',
  },
  {
    title: '🔄 Never out of sync',
    body: 'Add photos to the album and the gallery re-hangs itself. One source of truth, two ways to visit it.',
  },
]

const Page = styled(Box)({
  position: 'relative',
  minHeight: '100vh',
  overflow: 'hidden',
  background: 'var(--ds-night-canvas)',
  color: 'var(--ds-night-ink)',
  fontFamily: 'var(--ds-font)',
  '&::before': {
    content: '""',
    position: 'fixed',
    inset: 0,
    zIndex: 0,
    pointerEvents: 'none',
    background: [
      'radial-gradient(ellipse 55% 45% at 50% 0%, rgba(190,157,253,0.16), transparent 62%)',
      'radial-gradient(ellipse 45% 40% at 15% 90%, rgba(255,207,125,0.1), transparent 60%)',
    ].join(', '),
  },
})

const TopBar = styled(Box)({
  position: 'relative',
  zIndex: 5,
  height: 60,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0 26px',
  borderBottom: '1px solid var(--ds-night-border)',
})

const LogoRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 9,
  fontSize: 16.5,
  fontWeight: 750,
  letterSpacing: '-0.03em',
  color: 'var(--ds-night-ink)',
  cursor: 'pointer',
  userSelect: 'none',
})

const Hero = styled(Box)({
  position: 'relative',
  zIndex: 2,
  textAlign: 'center',
  padding: '68px 30px 40px',
})

const HeroTitle = styled(Typography)({
  fontSize: 'clamp(30px, 4.4vw, 46px)',
  fontWeight: 720,
  letterSpacing: '-0.035em',
  lineHeight: 1.1,
  marginTop: 14,
})

const HeroSub = styled(Typography)({
  margin: '18px auto 0',
  maxWidth: 560,
  fontSize: 15.5,
  lineHeight: 1.65,
  color: 'var(--ds-night-ink-muted)',
  '& b': { color: 'var(--ds-night-ink)' },
})

const RoomFrame = styled(Box)({
  position: 'relative',
  zIndex: 2,
  width: 'min(960px, calc(100% - 56px))',
  margin: '40px auto 0',
  borderRadius: 'var(--ds-radius-xl)',
  overflow: 'hidden',
  aspectRatio: '16 / 9',
  background: '#120e1b',
  boxShadow: 'var(--ds-shadow-night), 0 0 90px rgba(190,157,253,0.18)',
})

const Chip = styled(Box)({
  position: 'absolute',
  zIndex: 10,
  display: 'flex',
  alignItems: 'center',
  gap: 7,
  padding: '7px 13px',
  borderRadius: 999,
  background: 'rgba(23,22,29,0.75)',
  border: '1px solid var(--ds-night-border)',
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 8.5,
  letterSpacing: '0.1em',
  color: 'var(--ds-night-ink-muted)',
  whiteSpace: 'nowrap',
})

const EngineChip = styled(Chip)({ top: 16, left: 18, color: '#ffcf7d' })

const VisitorsChip = styled(Chip)({
  top: 16,
  right: 18,
  '&::before': {
    content: '""',
    width: 6,
    height: 6,
    borderRadius: '50%',
    background: '#01dafe',
    boxShadow: '0 0 8px #01dafe',
  },
})

const FocusChip = styled(Box, { shouldForwardProp: (p) => p !== 'visible' })<{ visible: boolean }>(
  ({ visible }) => ({
    position: 'absolute',
    zIndex: 9,
    left: 16,
    bottom: 64,
    maxWidth: 340,
    padding: '10px 14px',
    borderRadius: 13,
    background: 'rgba(23,22,29,0.82)',
    border: '1px solid var(--ds-night-border)',
    backdropFilter: 'blur(14px)',
    WebkitBackdropFilter: 'blur(14px)',
    opacity: visible ? 1 : 0,
    transition: 'opacity 300ms cubic-bezier(0.16, 1, 0.3, 1)',
  }),
)

const Hud = styled(Box)({
  position: 'absolute',
  left: '50%',
  bottom: 16,
  transform: 'translateX(-50%)',
  zIndex: 10,
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '8px 10px 8px 8px',
  borderRadius: 999,
  background: 'rgba(23,22,29,0.82)',
  border: '1px solid var(--ds-night-border)',
  backdropFilter: 'blur(14px)',
  WebkitBackdropFilter: 'blur(14px)',
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 9,
  letterSpacing: '0.1em',
  color: 'var(--ds-night-ink-muted)',
  whiteSpace: 'nowrap',
})

const KbBtn = styled('button')({
  all: 'unset',
  border: '1px solid var(--ds-night-border)',
  borderRadius: 5,
  padding: '2px 7px',
  background: 'rgba(244,243,246,0.06)',
  color: 'var(--ds-night-ink)',
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 9,
  letterSpacing: '0.1em',
  cursor: 'pointer',
  transition: 'background 150ms ease',
  '&:hover': { background: 'rgba(244,243,246,0.16)' },
})

const Kb = styled('span')({
  border: '1px solid var(--ds-night-border)',
  borderRadius: 5,
  padding: '2px 7px',
  background: 'rgba(244,243,246,0.06)',
})

const StepsSection = styled(Box)({
  position: 'relative',
  zIndex: 2,
  maxWidth: 1040,
  margin: '74px auto 0',
  padding: '0 32px',
})

const StepsHead = styled(Box)({
  textAlign: 'center',
  marginBottom: 34,
  '& h2': { fontSize: 30, fontWeight: 720, letterSpacing: '-0.035em', marginTop: 12 },
  '& p': { marginTop: 12, color: 'var(--ds-night-ink-muted)', fontSize: 14 },
})

const StepsGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(6, 1fr)',
  gap: 11,
  '@media (max-width: 960px)': { gridTemplateColumns: 'repeat(3, 1fr)' },
})

const StepCard = styled(Box)({
  border: '1px solid var(--ds-night-border)',
  borderRadius: 'var(--ds-radius-lg)',
  padding: '15px 13px 17px',
  background: 'rgba(23,22,29,0.6)',
  backdropFilter: 'blur(8px)',
  textAlign: 'center',
  transition: 'all 220ms cubic-bezier(0.16, 1, 0.3, 1)',
  '&:hover': { borderColor: 'rgba(190,157,253,0.45)', transform: 'translateY(-3px)' },
  '& .n': { fontFamily: 'var(--ds-font-mono)', fontSize: 8.5, letterSpacing: '0.12em', color: 'var(--ds-night-ink-subtle)' },
  '& .sw': { height: 52, margin: '11px 0', borderRadius: 10, overflow: 'hidden', position: 'relative' },
  '& .t': { fontSize: 12.5, fontWeight: 660 },
  '& .s': { fontSize: 10, color: 'var(--ds-night-ink-subtle)', marginTop: 3, fontFamily: 'var(--ds-font-mono)', letterSpacing: '0.04em' },
})

const SWATCH_STYLES: Record<string, React.CSSProperties> = {
  scene: { background: 'linear-gradient(180deg, #3d3450 0%, #221b31 100%)' },
  frame: { background: '#221b31', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  color: {
    background: 'linear-gradient(90deg, #f2ede4 0 25%, #cdc3d8 0 50%, #7a6d92 0 75%, #3a3050 0 100%)',
  },
  material: { background: 'repeating-linear-gradient(90deg, #6d5335 0 9px, #7d6141 9px 18px)' },
  prints: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 3,
    padding: 5,
    background: '#221b31',
  },
  matting: { background: '#f5f0e8', display: 'flex', alignItems: 'center', justifyContent: 'center' },
}

function StepSwatch({ kind }: { kind: string }) {
  if (kind === 'frame') {
    return (
      <Box className='sw' style={SWATCH_STYLES.frame}>
        <Box sx={{ width: 32, height: 32, border: '4px solid #d8c49a', boxShadow: 'inset 0 0 0 2px #221b31' }} />
      </Box>
    )
  }
  if (kind === 'prints') {
    return (
      <Box className='sw' style={SWATCH_STYLES.prints}>
        {Array.from({ length: 6 }).map((_, i) => (
          <Box key={i} sx={{ background: '#d8c49a', borderRadius: '2px', opacity: 0.85 }} />
        ))}
      </Box>
    )
  }
  if (kind === 'matting') {
    return (
      <Box className='sw' style={SWATCH_STYLES.matting}>
        <Box sx={{ width: 22, height: 22, background: '#8a8378', boxShadow: '0 0 0 8px #fff, 0 0 0 9px #ddd' }} />
      </Box>
    )
  }
  return <Box className='sw' style={SWATCH_STYLES[kind]} />
}

const ValsGrid = styled(Box)({
  position: 'relative',
  zIndex: 2,
  maxWidth: 1040,
  margin: '60px auto 0',
  padding: '0 32px',
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: 16,
  '@media (max-width: 860px)': { gridTemplateColumns: '1fr' },
})

const ValCard = styled(Box)({
  padding: 22,
  borderRadius: 'var(--ds-radius-lg)',
  border: '1px solid var(--ds-night-border)',
  background: 'rgba(23,22,29,0.5)',
  '& .t': { fontSize: 14, fontWeight: 680, display: 'flex', alignItems: 'center', gap: 9 },
  '& .s': { marginTop: 7, fontSize: 12.5, lineHeight: 1.6, color: 'var(--ds-night-ink-muted)' },
})

const CtaSection = styled(Box)({
  position: 'relative',
  zIndex: 2,
  textAlign: 'center',
  padding: '70px 30px 90px',
})

const PlanNote = styled(Typography)({
  marginTop: 16,
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 9,
  letterSpacing: '0.12em',
  color: 'var(--ds-night-ink-subtle)',
  textTransform: 'uppercase',
})

export function Try3DScreen() {
  const navigate = useNavigate()
  const roomRef = useRef<RoomWalkthroughHandle>(null)
  const roomAnchorRef = useRef<HTMLDivElement>(null)
  const [focus, setFocus] = useState<RoomFocus | null>(null)
  const [playing, setPlaying] = useState(true)

  const onToggleTour = () => roomRef.current?.toggleTour()
  const onWatch = () => roomAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })

  return (
    <Page>
      <TopBar>
        <LogoRow onClick={() => navigate('/albums')}>
          <LogoMark size={20} />
          {APP_NAME}
        </LogoRow>
        <Typography
          sx={{
            fontFamily: 'var(--ds-font-mono)',
            fontSize: 9.5,
            letterSpacing: '0.12em',
            color: 'var(--ds-night-ink-subtle)',
          }}
        >
          LIVE 3D PREVIEW
        </Typography>
        <Button variant='night' size='sm' onClick={() => navigate('/albums')}>
          ← Back to albums
        </Button>
      </TopBar>

      <Hero>
        <GradientText variant='eyebrow'>Included with Gallery plan</GradientText>
        <HeroTitle>
          Your album, hung in a
          <br />
          <GradientText>room you can walk through.</GradientText>
        </HeroTitle>
        <HeroSub>
          One tap. Your photos framed, matted, and lit on real walls — and everyone holding your key
          can walk in from any device. No downloads, no 3D skills.{' '}
          <b>The room below is real — drag it.</b>
        </HeroSub>
        <Box sx={{ mt: 3.5, display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button variant='spectrum' size='lg' onClick={() => navigate('/albums')}>
            ◈ Build my gallery →
          </Button>
          <Button variant='night' size='lg' onClick={onWatch}>
            Watch a 40s walkthrough
          </Button>
        </Box>
      </Hero>

      <RoomFrame ref={roomAnchorRef}>
        <RoomWalkthrough3D
          ref={roomRef}
          artworks={ARTWORKS}
          imageBase='/marketing/landing-v2'
          onFocusChange={setFocus}
          onPlayingChange={setPlaying}
        >
          <EngineChip>◈ LIVE 3D — BABYLON.JS · YOUR ACTUAL PHOTOS</EngineChip>
          <VisitorsChip>PRIYA &amp; NOAH ARE IN THIS GALLERY NOW</VisitorsChip>
          <FocusChip visible={!!focus}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
              <Typography
                sx={{ fontFamily: 'var(--ds-font-mono)', fontSize: 8, letterSpacing: '0.12em', color: '#ffcf7d' }}
              >
                {focus ? `${String(focus.index + 1).padStart(2, '0')} / ${String(ARTWORKS.length).padStart(2, '0')}` : '—'}
              </Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 680, letterSpacing: '-0.015em', color: 'var(--ds-night-ink)' }}>
                {focus?.name ?? '—'}
              </Typography>
            </Box>
            <Typography sx={{ mt: 0.4, fontFamily: 'var(--ds-font-mono)', fontSize: 8.5, letterSpacing: '0.08em', color: 'var(--ds-night-ink-muted)' }}>
              {focus
                ? `❤ ${focus.loves} · 💬 ${focus.cms}${focus.voice ? ' · ▶ VOICE NOTE' : ''}${focus.pin ? ' · ◈ 1 PINNED' : ''}`
                : '—'}
            </Typography>
          </FocusChip>
          <Hud>
            <KbBtn type='button' onClick={onToggleTour}>
              {playing ? '⏸ PAUSE TOUR' : '▶ RESUME TOUR'}
            </KbBtn>
            <span>
              <Kb>DRAG</Kb> LOOK AROUND · COMMENTS HANG WHERE THEY WERE PINNED · SAME KEY AS THE ALBUM
            </span>
          </Hud>
        </RoomWalkthrough3D>
      </RoomFrame>

      <StepsSection>
        <StepsHead>
          <Typography
            sx={{
              fontFamily: 'var(--ds-font-mono)',
              fontSize: 10,
              letterSpacing: '0.14em',
              color: 'var(--ds-night-ink-subtle)',
              textTransform: 'uppercase',
            }}
          >
            The wizard — six choices, five minutes
          </Typography>
          <Typography component='h2'>You art-direct. We hang.</Typography>
          <Typography component='p'>Every step previews live with your actual photos.</Typography>
        </StepsHead>
        <StepsGrid>
          {WIZARD_STEPS.map((s) => (
            <StepCard key={s.n}>
              <div className='n'>{s.n}</div>
              <StepSwatch kind={s.swatch} />
              <div className='t'>{s.label}</div>
              <div className='s'>{s.sub}</div>
            </StepCard>
          ))}
        </StepsGrid>
      </StepsSection>

      <ValsGrid>
        {VALUES.map((v) => (
          <ValCard key={v.title}>
            <div className='t'>{v.title}</div>
            <div className='s'>{v.body}</div>
          </ValCard>
        ))}
      </ValsGrid>

      <CtaSection>
        <Typography sx={{ fontSize: { xs: 26, md: 34 }, fontWeight: 720, letterSpacing: '-0.03em' }}>
          The wow is one tap away.
        </Typography>
        <Box sx={{ mt: 3.25, display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button variant='spectrum' size='lg' onClick={() => navigate('/albums')}>
            ◈ Build my gallery →
          </Button>
          <Button variant='night' size='lg' onClick={() => navigate('/albums')}>
            See Gallery plan
          </Button>
        </Box>
        <PlanNote>Included in Gallery &amp; Studio · Your album is already compatible</PlanNote>
      </CtaSection>
    </Page>
  )
}
